/**
 * Parser for the unicode-tree grammar used by the short-format
 * `*.sheet[short].json` fixtures (the repository's fixture sheets, whose
 * tree spec documents the grammar).
 *
 * The fixture has three structural fields:
 *  - `sheet`    — server-backed rows the wire emits (no hydration).
 *  - `hydrated` — the fully-hydrated tree; renderer and alias
 *                 children sit directly under their parent row (no
 *                 marker line — the old `(hydrated …)` marker format
 *                 is retired).
 *  - `rendered` — free-form text tree describing on-screen output.
 *
 * `parseTree` returns a typed row tree for `sheet` or `hydrated`.
 * `parseRendered` is a looser tree-of-text parser for `rendered`.
 */

import type { Option } from "../core/option.ts";
import { elementAt } from "../core/checked-index.ts";

// ─── Types ────────────────────────────────────────────────────────

/**
 * One node of a parsed unicode sheet tree (root, row or cell), the shape the short-format fixtures
 * and `HydratedTree.build` share.
 */
export type ParsedTree =
  | { kind: "root"; name: string; children: ParsedTree[] }
  | {
      kind: "row";
      type: string;
      attrs: Record<string, string>;
      label: Option<string>;
      children: ParsedTree[];
    }
  | {
      /**
       * Cell node per TREE-SPEC §3.4 — appears as a deeper child
       * of a row node in rendered output. Emitted by builders
       * that walk a `RenderScene`'s cells; not produced by the
       * strict-grammar `parseTree` (which is sheet/hydrated only).
       *
       * Payload format on serialize:
       *   `<columnLabel>: <value>[ <glyph>]?`
       */
      kind: "cell";
      columnLabel: string;
      value: string;
      glyph?: "ƒ" | "↻" | "!" | "↗";
      children: ParsedTree[];
    };

/**
 * One node of a parsed rendered tree (its text and children), as the short-format fixture's
 * `rendered` field states it.
 */
export type ParsedRendered = {
  text: string;
  children: ParsedRendered[];
};

/**
 * Thrown when a short-format fixture's unicode tree does not parse, naming the line and what was
 * expected.
 */
export class TreeParseError extends Error {
  constructor(
    message: string,
    public readonly line: Option<string>,
  ) {
    super(line ? `${message}\n  line: ${line}` : message);
  }
}

// ─── Depth + tree-char extraction ─────────────────────────────────

const INDENT_CONTINUATION = "\u2502   "; // "│   " (vertical bar + 3 spaces)
const INDENT_BLANK = "    "; //          four spaces
const BRANCH_MID = "\u251C\u2500\u2500 "; // "├── "
const BRANCH_END = "\u2514\u2500\u2500 "; // "└── "

interface LineStructure {
  /** Zero for root; 1+ for rows/markers. */
  depth: number;
  /** `null` for root lines, `mid` for `├──`, `end` for `└──`. */
  treeChar: Option<"mid" | "end">;
  /** The remainder of the line after the tree-char. */
  payload: string;
}

function parseLineStructure(line: string): LineStructure {
  let rest = line;
  let depth = 0;

  while (rest.length >= 4) {
    const prefix = rest.slice(0, 4);
    if (prefix === INDENT_CONTINUATION || prefix === INDENT_BLANK) {
      rest = rest.slice(4);
      depth++;
      continue;
    }
    if (prefix === BRANCH_MID) {
      return { depth: depth + 1, treeChar: "mid", payload: rest.slice(4) };
    }
    if (prefix === BRANCH_END) {
      return { depth: depth + 1, treeChar: "end", payload: rest.slice(4) };
    }
    break;
  }

  // No tree-char found — root line.
  return { depth, treeChar: null, payload: rest };
}

// ─── Row payload parsing ──────────────────────────────────────────

function parseRowPayload(
  payload: string,
): Omit<Extract<ParsedTree, { kind: "row" }>, "children"> {
  const trimmed = payload.trim();
  if (trimmed.length === 0) {
    throw new TreeParseError("empty payload", payload);
  }

  // Row: type [attrs]  "label"
  const [type, afterType] = readType(trimmed);
  let rest = afterType.trimStart();
  let attrs: Record<string, string> = {};
  if (rest.startsWith("[")) {
    const [parsed, consumed] = readAttrs(rest);
    attrs = parsed;
    rest = rest.slice(consumed).trimStart();
  }
  let label: Option<string> = null;
  if (rest.startsWith('"')) {
    const [parsed, consumed] = readQuotedString(rest);
    label = parsed;
    rest = rest.slice(consumed).trimStart();
  }

  if (rest.length > 0) {
    // Trailing unparsed text — keep it on `label` for visibility.
    label = label != null ? `${label} ${rest}` : rest;
  }

  return { kind: "row", type, attrs, label };
}

function readType(src: string): [string, string] {
  // Type runs until the first whitespace.
  let i = 0;
  while (i < src.length && !isWhitespace(elementAt(src, i, "parse-tree: src"))) i++;
  if (i === 0) throw new TreeParseError("empty type", src);
  return [src.slice(0, i), src.slice(i)];
}

function readAttrs(src: string): [Record<string, string>, number] {
  // src[0] === "["
  let pos = 1;
  let depth = 0;
  let inString: false | '"' = false;
  let currentKey = "";
  let currentValue = "";
  let inKey = true;
  const attrs: Record<string, string> = {};

  const flush = () => {
    const k = currentKey.trim();
    const v = currentValue.trim();
    if (k.length > 0) attrs[k] = v;
    currentKey = "";
    currentValue = "";
    inKey = true;
  };

  while (pos < src.length) {
    const c = elementAt(src, pos, "parse-tree: src");

    // String handling — don't let ",]=" inside strings terminate.
    if (inString) {
      if (c === "\\" && pos + 1 < src.length) {
        const toAppend = c + elementAt(src, pos + 1, "parse-tree: src");
        if (inKey) currentKey += toAppend;
        else currentValue += toAppend;
        pos += 2;
        continue;
      }
      if (c === inString) {
        inString = false;
      }
      if (inKey) currentKey += c;
      else currentValue += c;
      pos++;
      continue;
    }
    if (c === '"') {
      inString = '"';
      if (inKey) currentKey += c;
      else currentValue += c;
      pos++;
      continue;
    }

    // Top-level (depth=0) delimiters.
    if (depth === 0) {
      if (c === "]") {
        flush();
        return [attrs, pos + 1];
      }
      if (c === ",") {
        flush();
        pos++;
        continue;
      }
      if (c === "=" && inKey) {
        inKey = false;
        pos++;
        continue;
      }
    }

    // Nested bracket depth tracking — preserves commas/=/]
    // inside objects/lists/Lisp as raw value text.
    if (c === "[" || c === "{" || c === "(") {
      depth++;
    } else if (c === "}" || c === ")") {
      depth--;
    } else if (c === "]" && depth > 0) {
      depth--;
    }

    if (inKey) currentKey += c;
    else currentValue += c;
    pos++;
  }

  throw new TreeParseError("unterminated attrs block", src);
}

function readQuotedString(src: string): [string, number] {
  // src[0] === '"'
  let pos = 1;
  let value = "";
  while (pos < src.length) {
    const c = elementAt(src, pos, "parse-tree: src");
    if (c === "\\" && pos + 1 < src.length) {
      const next = elementAt(src, pos + 1, "parse-tree: src");
      value += next === "n" ? "\n" : next;
      pos += 2;
      continue;
    }
    if (c === '"') return [value, pos + 1];
    value += c;
    pos++;
  }
  throw new TreeParseError("unterminated quoted string", src);
}

function isWhitespace(c: string): boolean {
  return c === " " || c === "\t";
}

// ─── Tree assembly ────────────────────────────────────────────────

/**
 * Parse a `sheet` or `hydrated` tree field from a fixture into a
 * structured tree. The returned root's `children[0]` is typically
 * the outermost row under the `sheet` root.
 */
function parseTree(lines: readonly string[]): ParsedTree {
  if (lines.length === 0) {
    throw new TreeParseError("empty tree", null);
  }

  // Stack holds tree nodes whose next sibling OR child should be
  // added at `stack.length` depth. The root sits at index 0.
  const root: ParsedTree = {
    kind: "root",
    name: elementAt(lines, 0, "parse-tree: lines").trim(),
    children: [],
  };
  const structure0 = parseLineStructure(elementAt(lines, 0, "parse-tree: lines"));
  if (structure0.treeChar != null) {
    throw new TreeParseError(
      "first line must be the root (no tree-char)",
      elementAt(lines, 0, "parse-tree: lines"),
    );
  }

  const stack: ParsedTree[] = [root];

  for (let i = 1; i < lines.length; i++) {
    const line = elementAt(lines, i, "parse-tree: lines");
    if (line.trim() === "") continue;

    const struct = parseLineStructure(line);
    if (struct.treeChar == null) {
      throw new TreeParseError(
        "non-root line missing tree-char (├── or └──)",
        line,
      );
    }

    const node: ParsedTree = {
      ...parseRowPayload(struct.payload),
      children: [],
    };

    while (stack.length > struct.depth) stack.pop();
    if (stack.length === 0) {
      throw new TreeParseError(
        `line at depth ${struct.depth} has no ancestor in scope`,
        line,
      );
    }

    const parent = elementAt(stack, stack.length - 1, "parse-tree: stack");
    parent.children.push(node);
    stack.push(node);
  }

  return root;
}

// ─── Rendered-tree parser (text-only payloads) ───────────────────

/**
 * Parse the `rendered` field. Looser grammar — payloads are
 * free-form text (no attrs, no markers). Structure mirrors the
 * `tree` format.
 */
function parseRendered(lines: readonly string[]): ParsedRendered {
  if (lines.length === 0) throw new TreeParseError("empty rendered", null);

  const structure0 = parseLineStructure(elementAt(lines, 0, "parse-tree: lines"));
  if (structure0.treeChar != null) {
    throw new TreeParseError(
      "rendered root missing (first line must be bare text)",
      elementAt(lines, 0, "parse-tree: lines"),
    );
  }

  const root: ParsedRendered = {
    text: structure0.payload.trim(),
    children: [],
  };
  const stack: ParsedRendered[] = [root];

  for (let i = 1; i < lines.length; i++) {
    const line = elementAt(lines, i, "parse-tree: lines");
    if (line.trim() === "") continue;

    const struct = parseLineStructure(line);
    if (struct.treeChar == null) {
      // Rendered blocks occasionally embed blank-line-separated
      // prose paragraphs (e.g., "Fetch flow:"). Append as
      // sibling to root for visibility.
      root.children.push({ text: struct.payload.trim(), children: [] });
      continue;
    }

    const node: ParsedRendered = {
      text: struct.payload.trim(),
      children: [],
    };

    while (stack.length > struct.depth) stack.pop();
    if (stack.length === 0) {
      throw new TreeParseError(
        `rendered line at depth ${struct.depth} has no ancestor`,
        line,
      );
    }
    elementAt(stack, stack.length - 1, "parse-tree: stack").children.push(node);
    stack.push(node);
  }

  return root;
}

// ─── Walking helpers ──────────────────────────────────────────────

/** Visit every node in a ParsedTree depth-first. */
function* walkTree(node: ParsedTree): Generator<ParsedTree> {
  yield node;
  for (const child of node.children) yield* walkTree(child);
}

/** Find the first row node with a given type (DFS). */
function findRowByType(
  node: ParsedTree,
  type: string,
): Option<Extract<ParsedTree, { kind: "row" }>> {
  for (const n of walkTree(node)) {
    if (n.kind === "row" && n.type === type) return n;
  }
  return null;
}

/** Flatten all row nodes in document order. */
function allRows(
  node: ParsedTree,
): Extract<ParsedTree, { kind: "row" }>[] {
  const rows: Extract<ParsedTree, { kind: "row" }>[] = [];
  for (const n of walkTree(node)) {
    if (n.kind === "row") rows.push(n);
  }
  return rows;
}

// ─── Serializers ──────────────────────────────────────────────────

/**
 * Inverse of `parseTree`. Emits one string per tree node using the
 * §1 indent + tree-char grammar and the §2.2 row-payload grammar
 * (`type [attrs]?  "label"?`).
 *
 * Strict mode — accepts root + row variants only. `cell` nodes
 * throw (use `serializeRenderedTree` for trees with cells).
 *
 * Round-trip property (TREE-SPEC §8.2): `parseTree(serializeTree(t))`
 * is structurally equal to `t` for every well-formed `ParsedTree`
 * containing only root + row variants.
 */
function serializeTree(node: ParsedTree): string[] {
  const out: string[] = [];
  emitNode(node, 0, [], out, formatRowPayloadStrict);
  return out;
}

/**
 * Rendered-tree serializer per TREE-SPEC §3.4. Accepts root, row,
 * AND cell variants. Cell payloads format as
 * `<columnLabel>: <value>[ <glyph>]?` per §3.4. Row payloads use
 * the strict §2 grammar so attributes survive.
 *
 * Round-trip via `parseRendered` (loose grammar): every emitted
 * line is acceptable text payload, so the output → parseRendered
 * → ParsedRendered always succeeds.
 */
function serializeRenderedTree(node: ParsedTree): string[] {
  const out: string[] = [];
  emitNode(node, 0, [], out, formatPayloadAny);
  return out;
}

// ─── Metadata-columns serializer (TREE-SPEC §1.5) ─────────────────

/** Standard metadata column names (TREE-SPEC §1.5). */
export type TreeColumn = "date" | "delta" | "type" | "mode";

/** Per-node metadata bag, keyed by column name. Missing keys render as empty. */
export type TreeMetadata = Partial<Record<TreeColumn, string>>;

/**
 * Display labels for each `TreeColumn`. Used by the serializer to
 * label header columns; accepted by the parser as either the raw
 * name or the display label. The `delta` column uses the Δ symbol
 * to match the existing rendered-tree fixtures.
 */
const COLUMN_DISPLAY_LABEL: Record<TreeColumn, string> = {
  date: "date",
  delta: "Δ",
  type: "type",
  mode: "mode",
};

function parseColumnName(token: string): Option<TreeColumn> {
  const t = token.trim();
  for (const k of ["date", "delta", "type", "mode"] as TreeColumn[]) {
    if (t === k || t === COLUMN_DISPLAY_LABEL[k]) return k;
  }
  return null;
}

/**
 * Options for printing a rendered tree with metadata columns to its left, as the CLI's tree printer
 * does.
 */
export interface SerializeRenderedTreeWithColsOptions {
  /** Ordered metadata columns to render LEFT of the tree. */
  readonly columns: readonly TreeColumn[];
  /** Resolves per-node metadata. Returning `null` renders empty values. */
  readonly metadata: (node: ParsedTree) => Option<TreeMetadata>;
  /** Display label for the trailing tree column. Default: `"tree"`. */
  readonly treeColumnLabel?: string;
}

/**
 * Rendered-tree serializer with optional metadata columns left of
 * the tree (TREE-SPEC §1.5). When `columns` is empty, falls back
 * to plain `serializeRenderedTree` (no columns, no header).
 *
 * Output format (when columns present):
 *
 *   <col1Hdr>      │ <col2Hdr> │ ... │ <treeColLabel>
 *   ───────────────┼───────────┼─────┼────────────────
 *   <col1Val>      │ <col2Val> │ ... │ <treeLine>
 *   ...
 *
 * - Column widths computed in one pass: `max(header, …allValues)`
 *   per column. Ensures `│` separators align byte-for-byte.
 * - First metadata column has no leading padding; subsequent
 *   columns start with one space (after the `│`) per §1.5.
 * - Tree column has no trailing `│`; runs to end-of-line.
 * - Header separator (`─┼─` joins) appears once, immediately
 *   below the column-name header.
 */
function serializeRenderedTreeWithCols(
  tree: ParsedTree,
  opts: SerializeRenderedTreeWithColsOptions,
): string[] {
  const { columns, metadata, treeColumnLabel = "tree" } = opts;
  if (columns.length === 0) {
    return serializeRenderedTree(tree);
  }

  // Pass 1: compute tree-line per node + collect raw metadata.
  const treeLines = serializeRenderedTree(tree);
  const flatNodes: ParsedTree[] = [];
  flattenDfs(tree, flatNodes);
  if (flatNodes.length !== treeLines.length) {
    throw new Error(
      `serializeRenderedTreeWithCols: tree-line count (${treeLines.length}) ` +
        `mismatches node count (${flatNodes.length})`,
    );
  }

  const rowMetadata: string[][] = flatNodes.map((node) => {
    const m = metadata(node) ?? {};
    return columns.map((c) => m[c] ?? "");
  });

  // Pass 2: per-column content widths = max(headerLabel, …data).
  // Each col additionally carries 1 trailing pad (and 1 leading pad
  // for non-first cols) per TREE-SPEC §1.5.
  const headerLabels = columns.map((c) => COLUMN_DISPLAY_LABEL[c]);
  const contentWidths = columns.map((_c, i) => {
    let w = elementAt(headerLabels, i, "parse-tree: headerLabels").length;
    for (const row of rowMetadata) {
      if (elementAt(row, i, "parse-tree: row").length > w) w = elementAt(row, i, "parse-tree: row").length;
    }
    return w;
  });

  // Pass 3: emit header + separator + data lines.
  const out: string[] = [];
  out.push(formatMetaLine(headerLabels, contentWidths, treeColumnLabel));
  out.push(formatSeparator(contentWidths, treeLines, treeColumnLabel));
  for (let i = 0; i < flatNodes.length; i++) {
    out.push(formatMetaLine(elementAt(rowMetadata, i, "parse-tree: rowMetadata"), contentWidths, elementAt(treeLines, i, "parse-tree: treeLines")));
  }
  return out;
}

/**
 * Format one data row: `<col1> │ <col2> │ ... │ <treeRest>`. The
 * first metadata column has no leading padding; subsequent cols
 * use the ` │ ` separator (space + bar + space) per TREE-SPEC §1.5.
 * Each cell carries 1 trailing pad inside its content area.
 */
function formatMetaLine(
  values: readonly string[],
  contentWidths: readonly number[],
  treeRest: string,
): string {
  if (values.length === 0) return treeRest;
  // First col: <content padded right> + 1 trailing space
  let line = padRight(elementAt(values, 0, "parse-tree: values"), elementAt(contentWidths, 0, "parse-tree: contentWidths")) + " ";
  for (let i = 1; i < values.length; i++) {
    // Subsequent: │ + space + content padded + space
    line += `│ ${padRight(elementAt(values, i, "parse-tree: values"), elementAt(contentWidths, i, "parse-tree: contentWidths"))} `;
  }
  line += `│ ${treeRest}`;
  return line;
}

/** Format the header separator: `<dashes>┼<dashes>┼…┼<dashes-for-tree>`. */
function formatSeparator(
  contentWidths: readonly number[],
  treeLines: readonly string[],
  treeColumnLabel: string,
): string {
  const treeWidth = Math.max(
    treeColumnLabel.length,
    ...treeLines.map((l) => l.length),
  );
  // First col: contentWidth + 1 trailing-pad worth of dashes.
  // Non-first cols: contentWidth + 2 (1 leading + 1 trailing).
  let line = "─".repeat(elementAt(contentWidths, 0, "parse-tree: contentWidths") + 1);
  for (let i = 1; i < contentWidths.length; i++) {
    line += "┼" + "─".repeat(elementAt(contentWidths, i, "parse-tree: contentWidths") + 2);
  }
  // Tree side: ┼ + leading-space + dashes for treeWidth.
  line += "┼" + "─".repeat(treeWidth + 1);
  return line;
}

function flattenDfs(node: ParsedTree, out: ParsedTree[]): void {
  out.push(node);
  for (const c of node.children) flattenDfs(c, out);
}

function padRight(text: string, width: number): string {
  if (text.length >= width) return text;
  return text + " ".repeat(width - text.length);
}

// ─── parseRenderedTree (inverse of serializeRenderedTreeWithCols) ─

/**
 * Envelope returned by `parseRenderedTree` / consumed by
 * `serializeRenderedTreeWithCols` (TREE-SPEC §4.3).
 */
export interface RenderedTree {
  /** Names of metadata columns in left-to-right order. Empty when
   *  the input is plain-tree (no `│` in the first line). */
  readonly columns: readonly TreeColumn[];
  /** Parsed tree from the rightmost (tree) column. */
  readonly root: ParsedTree;
  /** Per-node metadata column values. Missing keys → no metadata. */
  readonly metadata: ReadonlyMap<ParsedTree, TreeMetadata>;
}

/**
 * Inverse of `serializeRenderedTreeWithCols`. Consumes both the
 * plain rendered-tree format (no metadata cols) and the §1.5
 * metadata-columns format. Auto-detects which by checking whether
 * the first line contains a `│` separator.
 *
 * Round-trip property:
 *   parseRenderedTree(serializeRenderedTreeWithCols(t, opts))
 *     ≡ structurally equal {columns, root, metadata} for every
 *       well-formed input.
 *
 * Tree-payload disambiguation: a payload matching `/^[^:]*:( |$)/`
 * (text + colon + space-or-EOL) is parsed as a `cell` node;
 * everything else is parsed as a `row` node via the strict §2
 * grammar. Wire row payloads never contain `colon + space` at
 * the top level (colons inside `[attrs]` are wrapped).
 */
function parseRenderedTree(lines: readonly string[]): RenderedTree {
  if (lines.length === 0) {
    throw new TreeParseError("empty rendered tree", null);
  }
  const header = elementAt(lines, 0, "parse-tree: lines");
  if (!header.includes("│")) {
    // Plain tree (no metadata cols).
    const root = parseTreeAllowingCells(lines);
    return { columns: [], root, metadata: new Map() };
  }

  // Metadata-cols format.
  const sepPositions: number[] = [];
  for (let i = 0; i < header.length; i++) {
    if (header[i] === "│") sepPositions.push(i);
  }
  if (sepPositions.length === 0) {
    throw new TreeParseError(
      "metadata-cols header has no │ separators",
      header,
    );
  }
  if (lines.length < 2) {
    throw new TreeParseError(
      "metadata-cols format requires header + separator line",
      header,
    );
  }
  // Header column names: split header by `│` positions, trim each.
  // First seg is the first column; subsequent segs include the `│ `
  // leading space already consumed via the position-based split.
  const headerSegs = splitByPositions(header, sepPositions);
  // Last seg is the tree column label (we don't validate name).
  const columns: TreeColumn[] = [];
  for (let i = 0; i < headerSegs.length - 1; i++) {
    const name = elementAt(headerSegs, i, "parse-tree: headerSegs").trim();
    const col = parseColumnName(name);
    if (col == null) {
      throw new TreeParseError(
        `unknown metadata column name "${name}"`,
        header,
      );
    }
    columns.push(col);
  }

  // Skip line[1] (separator). Treat line[2..] as data rows.
  const treeLines: string[] = [];
  const perLineMetadata: TreeMetadata[] = [];
  for (let i = 2; i < lines.length; i++) {
    const segs = splitByPositions(elementAt(lines, i, "parse-tree: lines"), sepPositions);
    if (segs.length !== columns.length + 1) {
      throw new TreeParseError(
        `data line has ${segs.length} segments; expected ${
          columns.length + 1
        }`,
        elementAt(lines, i, "parse-tree: lines"),
      );
    }
    // Last seg is the tree-line. Strip the leading space added by the `│ ` separator.
    let treeLine = elementAt(segs, segs.length - 1, "parse-tree: segs");
    if (treeLine.startsWith(" ")) treeLine = treeLine.slice(1);
    treeLines.push(treeLine);

    const meta: TreeMetadata = {};
    for (let c = 0; c < columns.length; c++) {
      const val = elementAt(segs, c, "parse-tree: segs").trim();
      if (val.length > 0) meta[elementAt(columns, c, "parse-tree: columns")] = val;
    }
    perLineMetadata.push(meta);
  }

  const root = parseTreeAllowingCells(treeLines);
  const flat: ParsedTree[] = [];
  flattenDfs(root, flat);
  if (flat.length !== perLineMetadata.length) {
    throw new TreeParseError(
      `tree node count (${flat.length}) mismatches metadata row count (${perLineMetadata.length})`,
      header,
    );
  }
  const metadata = new Map<ParsedTree, TreeMetadata>();
  for (let i = 0; i < flat.length; i++) {
    if (Object.keys(elementAt(perLineMetadata, i, "parse-tree: perLineMetadata")).length > 0) {
      metadata.set(elementAt(flat, i, "parse-tree: flat"), elementAt(perLineMetadata, i, "parse-tree: perLineMetadata"));
    }
  }
  return { columns, root, metadata };
}

/**
 * Tree-line parser that accepts both row payloads (strict §2) and
 * cell payloads (§3.4). Used internally by `parseRenderedTree`.
 */
function parseTreeAllowingCells(lines: readonly string[]): ParsedTree {
  if (lines.length === 0) {
    throw new TreeParseError("empty tree", null);
  }
  const root: ParsedTree = {
    kind: "root",
    name: elementAt(lines, 0, "parse-tree: lines").trim(),
    children: [],
  };
  const structure0 = parseLineStructure(elementAt(lines, 0, "parse-tree: lines"));
  if (structure0.treeChar != null) {
    throw new TreeParseError(
      "first line must be the root (no tree-char)",
      elementAt(lines, 0, "parse-tree: lines"),
    );
  }
  const stack: ParsedTree[] = [root];
  for (let i = 1; i < lines.length; i++) {
    const line = elementAt(lines, i, "parse-tree: lines");
    if (line.trim() === "") continue;
    const struct = parseLineStructure(line);
    if (struct.treeChar == null) {
      throw new TreeParseError(
        "non-root line missing tree-char (├── or └──)",
        line,
      );
    }
    const node = parsePayloadAny(struct.payload);
    while (stack.length > struct.depth) stack.pop();
    if (stack.length === 0) {
      throw new TreeParseError(
        `line at depth ${struct.depth} has no ancestor in scope`,
        line,
      );
    }
    elementAt(stack, stack.length - 1, "parse-tree: stack").children.push(node);
    stack.push(node);
  }
  return root;
}

const CELL_PAYLOAD_RE = /^[^:]*:( |$)/;
const CELL_GLYPH_RE = / ([ƒ↻!↗])$/;

function parsePayloadAny(payload: string): ParsedTree {
  if (CELL_PAYLOAD_RE.test(payload)) {
    return parseCellPayload(payload);
  }
  return { ...parseRowPayload(payload), children: [] };
}

function parseCellPayload(payload: string): ParsedTree {
  const idx = payload.search(/:( |$)/);
  const columnLabel = payload.slice(0, idx);
  let after = payload.slice(idx + 1);
  if (after.startsWith(" ")) after = after.slice(1);
  const glyphMatch = after.match(CELL_GLYPH_RE);
  let value = after;
  let glyph: Option<"ƒ" | "↻" | "!" | "↗"> = null;
  if (glyphMatch) {
    glyph = glyphMatch[1] as "ƒ" | "↻" | "!" | "↗";
    value = after.slice(0, after.length - glyphMatch[0].length);
  }
  return {
    kind: "cell",
    columnLabel,
    value,
    ...(glyph != null ? { glyph } : {}),
    children: [],
  };
}

function splitByPositions(line: string, positions: readonly number[]): string[] {
  const out: string[] = [];
  let start = 0;
  for (const pos of positions) {
    out.push(line.slice(start, pos));
    start = pos + 1; // skip the `│`
  }
  out.push(line.slice(start));
  return out;
}

/**
 * Inverse of `parseRendered`. Emits one string per node using the
 * §1 grammar with free-form text payloads.
 *
 * Round-trip property: `parseRendered(serializeRendered(r))` is
 * structurally equal to `r`.
 */
function serializeRendered(node: ParsedRendered): string[] {
  const out: string[] = [];
  emitRenderedNode(node, 0, [], out);
  return out;
}

function emitNode(
  node: ParsedTree,
  depth: number,
  ancestorIsLast: readonly boolean[],
  out: string[],
  format: (n: ParsedTree) => string,
): void {
  out.push(buildLinePrefix(depth, ancestorIsLast) + format(node));
  for (let i = 0; i < node.children.length; i++) {
    const child = elementAt(node.children, i, "parse-tree: node.children");
    const isLast = i === node.children.length - 1;
    emitNode(
      child,
      depth + 1,
      [...ancestorIsLast, isLast],
      out,
      format,
    );
  }
}

function emitRenderedNode(
  node: ParsedRendered,
  depth: number,
  ancestorIsLast: readonly boolean[],
  out: string[],
): void {
  out.push(buildLinePrefix(depth, ancestorIsLast) + node.text);
  for (let i = 0; i < node.children.length; i++) {
    const child = elementAt(node.children, i, "parse-tree: node.children");
    const isLast = i === node.children.length - 1;
    emitRenderedNode(child, depth + 1, [...ancestorIsLast, isLast], out);
  }
}

/**
 * Build the indent + tree-char prefix for a node at `depth` whose
 * ancestor at depth `k+1` had `ancestorIsLast[k]` set when emitted.
 *
 * - Root (depth 0): empty prefix.
 * - Depth 1: just the tree-char (`├── ` or `└── `).
 * - Depth d ≥ 2: (d-1) indent segments (one per ancestor between
 *   root and parent) + tree-char. Indent is `│   ` if that
 *   ancestor still has siblings below, `    ` otherwise.
 */
function buildLinePrefix(
  depth: number,
  ancestorIsLast: readonly boolean[],
): string {
  if (depth === 0) return "";
  let indent = "";
  for (let k = 0; k < depth - 1; k++) {
    indent += ancestorIsLast[k] ? INDENT_BLANK : INDENT_CONTINUATION;
  }
  const isLast = ancestorIsLast[depth - 1] === true;
  indent += isLast ? BRANCH_END : BRANCH_MID;
  return indent;
}

function formatRowPayloadStrict(node: ParsedTree): string {
  if (node.kind === "root") return node.name;
  if (node.kind === "cell") {
    throw new Error(
      `serializeTree: encountered cell node (columnLabel=${
        JSON.stringify(node.columnLabel)
      }). ` +
        `Cells are §3.4 grammar — use serializeRenderedTree for trees with cells.`,
    );
  }
  return formatRowPayload(node);
}

function formatPayloadAny(node: ParsedTree): string {
  if (node.kind === "root") return node.name;
  if (node.kind === "cell") return formatCellPayload(node);
  return formatRowPayload(node);
}

function formatRowPayload(
  node: Extract<ParsedTree, { kind: "row" }>,
): string {
  // Row payload: type [attrs]?  "label"?
  let payload = node.type;
  const attrEntries = Object.entries(node.attrs);
  if (attrEntries.length > 0) {
    const pairs = attrEntries.map(([k, v]) => `${k}=${v}`);
    payload += ` [${pairs.join(", ")}]`;
  }
  if (node.label != null) {
    payload += `  "${escapeLabel(node.label)}"`;
  }
  return payload;
}

function formatCellPayload(
  node: Extract<ParsedTree, { kind: "cell" }>,
): string {
  // Cell payload: <columnLabel>: <value>[ <glyph>]?
  // Empty value renders as `<columnLabel>:` with no trailing space.
  let payload = `${node.columnLabel}:`;
  if (node.value.length > 0) payload += ` ${node.value}`;
  if (node.glyph !== undefined) payload += ` ${node.glyph}`;
  return payload;
}

function escapeLabel(label: string): string {
  // Re-escape \ and " — the inverse of readQuotedString's unescape.
  return label.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

// ─── Diff (TREE-SPEC §9) ──────────────────────────────────────────

/** Path from the root: child indexes at each depth. Empty = root. */
export type TreePath = readonly number[];

/** Granularity of a node-level change. */
export interface TreeChangeKind {
  /** Row attrs differ between prev and next. */
  readonly attrsChanged: boolean;
  /** Row label differs. */
  readonly labelChanged: boolean;
  /** Cell value or glyph differs (always false until the cell variant lands). */
  readonly cellValueChanged: boolean;
  /** This node's children list shape changed (insert/remove of direct children). */
  readonly childrenChanged: boolean;
}

/** A single recorded difference between two trees. */
export type TreeChange =
  | { readonly tag: "insert"; readonly path: TreePath; readonly next: ParsedTree }
  | { readonly tag: "remove"; readonly path: TreePath; readonly prev: ParsedTree }
  | {
    readonly tag: "update";
    readonly path: TreePath;
    readonly prev: ParsedTree;
    readonly next: ParsedTree;
    readonly what: TreeChangeKind;
  };

/** The changes between two `ParsedTree` snapshots, which a `TreeWatcher` reports. */
export interface TreeDiff {
  readonly changes: readonly TreeChange[];
}

/**
 * Classify the difference between two trees per TREE-SPEC §9.
 *
 * `prev = null` ⇒ every node in `next` is an `insert` (rooted at
 * the root path `[]`). Subsequent diffs produce minimal patches.
 *
 * Sibling matching uses **identity** (TREE-SPEC §9.2):
 * - Root: name.
 * - Row: `(type, label, primary attribute)` where the primary
 *   attribute is `lookupKey` for sources, `template` for aliases,
 *   `formula` for formula rows, otherwise the first attr in
 *   iteration order. Falls back to position when no attrs exist.
 *
 * Reordered children with stable identity produce no changes;
 * structural reordering is treated as identity-preserving.
 */
function diffTree(
  prev: Option<ParsedTree>,
  next: ParsedTree,
): TreeDiff {
  const changes: TreeChange[] = [];
  if (prev == null) {
    changes.push({ tag: "insert", path: [], next });
    return { changes };
  }
  // Identity mismatch at root → treat as full replace (one remove + one insert)
  if (nodeIdentity(prev) !== nodeIdentity(next)) {
    changes.push({ tag: "remove", path: [], prev });
    changes.push({ tag: "insert", path: [], next });
    return { changes };
  }
  diffNode(prev, next, [], changes);
  return { changes };
}

function diffNode(
  prev: ParsedTree,
  next: ParsedTree,
  path: TreePath,
  out: TreeChange[],
): void {
  const what = {
    attrsChanged: false,
    labelChanged: false,
    cellValueChanged: false,
    childrenChanged: false,
  };

  if (prev.kind === "row" && next.kind === "row") {
    if (prev.label !== next.label) what.labelChanged = true;
    if (!attrsEqual(prev.attrs, next.attrs)) what.attrsChanged = true;
  } else if (prev.kind === "cell" && next.kind === "cell") {
    if (prev.value !== next.value || prev.glyph !== next.glyph) {
      what.cellValueChanged = true;
    }
  }

  // Pair children by identity; greedy first-match.
  const matched = new Set<number>();
  const childChanges: TreeChange[] = [];
  for (let nextIdx = 0; nextIdx < next.children.length; nextIdx++) {
    const nextChild = elementAt(next.children, nextIdx, "parse-tree: next.children");
    const nextId = nodeIdentity(nextChild);
    let matchedPrevIdx = -1;
    for (let prevIdx = 0; prevIdx < prev.children.length; prevIdx++) {
      if (matched.has(prevIdx)) continue;
      if (nodeIdentity(elementAt(prev.children, prevIdx, "parse-tree: prev.children")) === nextId) {
        matchedPrevIdx = prevIdx;
        break;
      }
    }
    if (matchedPrevIdx === -1) {
      childChanges.push({
        tag: "insert",
        path: [...path, nextIdx],
        next: nextChild,
      });
      what.childrenChanged = true;
    } else {
      matched.add(matchedPrevIdx);
      diffNode(
        elementAt(prev.children, matchedPrevIdx, "parse-tree: prev.children"),
        nextChild,
        [...path, nextIdx],
        childChanges,
      );
    }
  }
  for (let prevIdx = 0; prevIdx < prev.children.length; prevIdx++) {
    if (matched.has(prevIdx)) continue;
    childChanges.push({
      tag: "remove",
      path: [...path, prevIdx],
      prev: elementAt(prev.children, prevIdx, "parse-tree: prev.children"),
    });
    what.childrenChanged = true;
  }

  // Emit `update` for self if anything changed (incl. children list shape).
  if (
    what.attrsChanged ||
    what.labelChanged ||
    what.cellValueChanged ||
    what.childrenChanged
  ) {
    out.push({ tag: "update", path, prev, next, what });
  }
  for (const c of childChanges) out.push(c);
}

function nodeIdentity(node: ParsedTree): string {
  if (node.kind === "root") return `root|${node.name}`;
  if (node.kind === "cell") return `cell|${node.columnLabel}`;
  // Identity is (type, primary attribute). Label is NOT part of
  // identity — a label rename is an `update`, not insert + remove.
  // Sibling collisions (two rows with same type + no primary attr)
  // fall back to the iteration order via greedy first-match in
  // `diffNode`.
  return `row|${node.type}|${primaryAttrSignature(node)}`;
}

const PRIMARY_ATTR_KEYS_BY_TYPE: Record<string, readonly string[]> = {
  // Source rows: lookupKey is the wire identifier.
  source: ["lookupKey"],
  dataSource: ["lookupKey"],
  // Alias rows: template is what binds the hydrated subtree.
  alias: ["template"],
  // Formula rows: formula expression is the identity.
  formula: ["formula"],
};

function primaryAttrSignature(
  row: Extract<ParsedTree, { kind: "row" }>,
): string {
  const candidates = PRIMARY_ATTR_KEYS_BY_TYPE[row.type] ?? [];
  for (const key of candidates) {
    if (key in row.attrs) return `${key}=${row.attrs[key]}`;
  }
  // Generic fallbacks — try lookupKey then first iteration attr.
  if ("lookupKey" in row.attrs) return `lookupKey=${row.attrs.lookupKey}`;
  const first = Object.entries(row.attrs)[0];
  return first ? `${first[0]}=${first[1]}` : "";
}

function attrsEqual(
  a: Record<string, string>,
  b: Record<string, string>,
): boolean {
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  for (const k of ak) {
    if (!(k in b) || a[k] !== b[k]) return false;
  }
  return true;
}

// ── Namespaces ─────────────────────────────────────────────────────

const $parseTree = parseTree;
const $serializeTree = serializeTree;
const $findRowByType = findRowByType;
const $allRows = allRows;
const $diffTree = diffTree;
const $walkTree = walkTree;
const $parseRendered = parseRendered;
const $serializeRendered = serializeRendered;
const $parseRenderedTree = parseRenderedTree;
const $serializeRenderedTree = serializeRenderedTree;
const $serializeRenderedTreeWithCols = serializeRenderedTreeWithCols;

/**
 * Values that go with `ParsedTree`: `parse`, `serialize`, `findByType`,
 * `allRows`, `diff`, `walk`.
 */
export namespace ParsedTree {
  export const parse: (lines: readonly string[]) => ParsedTree = $parseTree;
  export const serialize: (node: ParsedTree) => string[] = $serializeTree;
  export const findByType: (node: ParsedTree, type: string) => Option<Extract<ParsedTree, { kind: "row"; }>> = $findRowByType;
  export const allRows: (node: ParsedTree) => Extract<ParsedTree, { kind: "row"; }>[] = $allRows;
  export const diff: (prev: Option<ParsedTree>, next: ParsedTree) => TreeDiff = $diffTree;
  export const walk: (node: ParsedTree) => Generator<ParsedTree> = $walkTree;
}

/** Values that go with `ParsedRendered`: `parse`, `serialize`. */
export namespace ParsedRendered {
  export const parse: (lines: readonly string[]) => ParsedRendered = $parseRendered;
  export const serialize: (node: ParsedRendered) => string[] = $serializeRendered;
}

/** Values that go with `RenderedTree`: `parse`, `serialize`, `serializeWithCols`. */
export namespace RenderedTree {
  export const parse: (lines: readonly string[]) => RenderedTree = $parseRenderedTree;
  export const serialize: (node: ParsedTree) => string[] = $serializeRenderedTree;
  export const serializeWithCols: (tree: ParsedTree, opts: SerializeRenderedTreeWithColsOptions) => string[] = $serializeRenderedTreeWithCols;
}
